import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table, Select, Dropdown, message } from 'antd';
import type { TableColumnsType, ThemeConfig, MenuProps } from 'antd';
import { fontFamily } from '../../theme/tokens';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  useGetUsersQuery,
  useDeleteUserMutation,
  useGetOrgNodesQuery,
  useLazyGetUserImportTemplateQuery,
  useImportUsersMutation,
  userName,
  userEmail,
  userPhone,
  userActive,
  userEmpId,
  userLastSeen,
  userAvatar,
  userHierarchyList,
  userRoleNames,
  type AppUser,
} from '../../features/users/usersApi';
import { useGetRolesQuery } from '../../features/roles/rolesApi';
import { useGetOrganizationsQuery } from '../../features/organizations/organizationsApi';
import {
  UploadIcon,
  DownloadIcon,
  PlusIcon,
  KebabIcon,
  EditSquareIcon,
  TrashIcon,
} from '../../assets/icons';

const TABLE_THEME: ThemeConfig = {
  token: { colorBorderSecondary: '#e2e8f0', fontFamily },
  components: {
    Table: {
      headerBg: 'rgba(248,250,252,0.5)',
      headerColor: '#64748b',
      headerSplitColor: 'transparent',
      borderColor: '#e2e8f0',
      cellPaddingBlock: 20,
      cellPaddingInline: 24,
      rowHoverBg: '#f8fafc',
      colorText: '#334155',
      fontSize: 14,
    },
  },
};

/** Per-role pill colours from the design (fallback = neutral). */
const ROLE_PILL: Record<string, string> = {
  'state admin': 'border-[#bfdbfe] bg-[#dbeafe] text-[#0061ff]',
  observer: 'border-[#e2e8f0] bg-[#f1f5f9] text-[#475569]',
  'it auditor': 'border-[#fed7aa] bg-[#fff7ed] text-[#c2410c]',
};
const rolePill = (role: string) =>
  ROLE_PILL[role.toLowerCase()] ?? 'border-[#e2e8f0] bg-[#f1f5f9] text-[#475569]';

/** Two-letter initials for the avatar fallback. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '—';
}

function RolePill({ role }: { role: string }) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-full border px-[11px] py-[3px] text-[10px] font-semibold uppercase tracking-[0.5px]',
        rolePill(role),
      ].join(' ')}
    >
      {role}
    </span>
  );
}

function StatusCell({ active, lastSeen }: { active: boolean; lastSeen: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span
        className={[
          'inline-flex items-center gap-1.5 rounded-full border px-[11px] py-[5px] text-xs font-medium capitalize',
          active
            ? 'border-[#bbf7d0] bg-[#f0fdf4] text-[#15803d]'
            : 'border-[#e2e8f0] bg-[#f1f5f9] text-[#475569]',
        ].join(' ')}
      >
        <span
          className={['size-1.5 rounded-full', active ? 'bg-[#22c55e]' : 'bg-[#94a3b8]'].join(' ')}
        />
        {active ? 'Active' : 'Inactive'}
      </span>
      {lastSeen && <span className="text-[10px] text-[#94a3b8]">Last: {lastSeen}</span>}
    </div>
  );
}

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const items: MenuProps['items'] = [
    { key: 'edit', label: 'Edit', icon: <EditSquareIcon className="size-4" /> },
    { type: 'divider' },
    { key: 'delete', label: 'Delete', danger: true, icon: <TrashIcon className="size-4" /> },
  ];
  const onClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'edit') onEdit();
    else if (key === 'delete') onDelete();
  };
  return (
    <Dropdown menu={{ items, onClick }} trigger={['click']} placement="bottomRight">
      <button
        type="button"
        aria-label="Row actions"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex size-8 items-center justify-center rounded-lg text-[#94a3b8] transition-colors hover:bg-brand-shade hover:text-brand"
      >
        <KebabIcon className="h-[15px] w-[3px] rotate-90" />
      </button>
    </Dropdown>
  );
}

const LABEL = 'text-[11px] font-semibold uppercase tracking-[0.55px] text-[#64748b]';

export default function UsersPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<number | ''>('');
  const [organization, setOrganization] = useState<number | ''>('');
  const [orgLevel, setOrgLevel] = useState<number | ''>('');
  const [status, setStatus] = useState<'active' | 'inactive' | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [userToDelete, setUserToDelete] = useState<AppUser | null>(null);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const isActive = status === 'active' ? true : status === 'inactive' ? false : '';
  const { data, isFetching } = useGetUsersQuery({
    search,
    isActive,
    role,
    organization,
    orgLevel,
    page,
    pageSize,
  });
  const { data: rolesData } = useGetRolesQuery({ all: true });
  const { data: orgsData } = useGetOrganizationsQuery({ all: true });
  const { data: orgNodes } = useGetOrgNodesQuery(
    { organization: organization as number },
    { skip: organization === '' },
  );
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [downloadTemplate, { isFetching: isDownloading }] = useLazyGetUserImportTemplateQuery();
  const [importUsers, { isLoading: isImporting }] = useImportUsersMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Server-generated template for the selected organization.
  const handleDownloadTemplate = async () => {
    if (organization === '') {
      message.warning('Select an organization first to download its template.');
      return;
    }
    try {
      const blob = await downloadTemplate({ organization }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-upload-template-org-${organization}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Could not download the template. Please try again.');
    }
  };

  // Bulk upload a filled template.
  const handleFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    try {
      const res = await importUsers({ file }).unwrap();
      const created = res?.created ?? 0;
      const failed = res?.failed ?? res?.errors?.length ?? 0;
      if (failed) {
        message.warning(`Import finished: ${created} added, ${failed} row(s) had errors.`);
      } else {
        message.success(res?.detail || `Import successful — ${created} user(s) added.`);
      }
    } catch {
      message.error('Bulk upload failed. Check the file format and try again.');
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Org-level options = the selected organization's nodes.
  const levelOptions = useMemo(
    () =>
      (orgNodes ?? []).map((n) => ({
        value: n.id,
        label: [n.level_name, n.org_name || n.name].filter(Boolean).join(' · ') || `Node ${n.id}`,
      })),
    [orgNodes],
  );

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete.id).unwrap();
      setUserToDelete(null);
    } catch {
      /* keep the modal open so the user can retry */
    }
  };

  const clearFilters = () => {
    setQuery('');
    setRole('');
    setOrganization('');
    setOrgLevel('');
    setStatus('');
    setPage(1);
  };

  const columns: TableColumnsType<AppUser> = [
    {
      title: 'User Info',
      key: 'user',
      width: 220,
      render: (_, u) => {
        const name = userName(u);
        const avatar = userAvatar(u);
        const empId = userEmpId(u);
        return (
          <div className="flex items-center gap-4">
            {avatar ? (
              <img src={avatar} alt={name} className="size-10 shrink-0 rounded-full object-cover" />
            ) : (
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-avatar2 text-[13px] font-semibold text-brand">
                {initials(name)}
              </span>
            )}
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-[#0f172a]">{name}</span>
              {empId && <span className="text-[11px] text-[#64748b]">ID: {empId}</span>}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 240,
      render: (_, u) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-sm text-[#334155]">{userEmail(u) || '—'}</span>
          <span className="text-xs text-[#64748b]">{userPhone(u) || '—'}</span>
        </div>
      ),
    },
    {
      title: 'Functional Roles',
      key: 'roles',
      width: 190,
      render: (_, u) => {
        const roles = userRoleNames(u);
        return (
          <div className="flex flex-col items-start gap-1.5">
            {roles.length > 0 ? (
              roles.map((r, i) => <RolePill key={`${r}-${i}`} role={r} />)
            ) : (
              <span className="text-sm text-[#94a3b8]">—</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Organization Hierarchy',
      key: 'org',
      width: 160,
      render: (_, u) => {
        const levels = userHierarchyList(u);
        return levels.length > 0 ? (
          <div className="flex flex-wrap items-start gap-1.5">
            {levels.map((l, i) => (
              <span
                key={`${l}-${i}`}
                className="rounded-md bg-[#eef4ff] px-2 py-0.5 text-xs font-medium text-brand"
              >
                {l}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-[#94a3b8]">—</span>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      align: 'center',
      render: (_, u) => <StatusCell active={userActive(u)} lastSeen={userLastSeen(u)} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_, u) => (
        <RowActions
          onEdit={() => navigate(`/admin/users/${u.id}/edit`)}
          onDelete={() => setUserToDelete(u)}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-9 text-ink">User Management</h1>
          <p className="text-sm text-ink-secondary">
            Manage administrative personnel, roles, and organizational hierarchies.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleFilePicked}
          />
          <button
            type="button"
            onClick={handleDownloadTemplate}
            disabled={isDownloading}
            className="flex items-center gap-2 rounded-md border border-[#cbd5e1] bg-white px-[17px] py-[9px] text-sm font-medium text-ink transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-60"
          >
            <DownloadIcon className="size-4" />
            {isDownloading ? 'Preparing…' : 'Download Template'}
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className="flex items-center gap-2 rounded-md border border-[#cbd5e1] bg-white px-[17px] py-[9px] text-sm font-medium text-ink transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-60"
          >
            <UploadIcon className="h-[14px] w-[12px]" />
            {isImporting ? 'Uploading…' : 'Bulk Upload'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/users/new')}
            className="flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            <PlusIcon className="size-3.5" />
            Create User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 pb-5 pt-6 shadow-card">
        <div className="flex items-end gap-4 overflow-x-auto">
          <div className="flex min-w-[140px] flex-1 flex-col gap-2">
            <span className={LABEL}>Name / Email</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="By Identity..."
              className="w-full rounded-md border border-[#cbd5e1] bg-white px-[13px] py-2.5 text-sm text-ink shadow-card placeholder:text-[#6b7280] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>

          <div className="flex w-[160px] shrink-0 flex-col gap-2">
            <span className={LABEL}>Role</span>
            <Select
              className="w-full"
              value={role === '' ? '' : role}
              onChange={(v) => {
                setRole(v as number | '');
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Roles' },
                ...(rolesData?.items ?? []).map((r) => ({ value: r.id, label: r.name })),
              ]}
            />
          </div>

          <div className="flex w-[160px] shrink-0 flex-col gap-2">
            <span className={LABEL}>Organization</span>
            <Select
              className="w-full"
              placeholder="All Organizations"
              value={organization === '' ? '' : organization}
              onChange={(v) => {
                setOrganization(v as number | '');
                setOrgLevel(''); // levels belong to the previous org
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Organizations' },
                ...(orgsData?.items ?? []).map((o) => ({ value: o.id, label: o.name })),
              ]}
            />
          </div>

          <div className="flex w-[160px] shrink-0 flex-col gap-2">
            <span className={LABEL}>Org Level</span>
            <Select
              className="w-full"
              placeholder={organization === '' ? 'Select organization' : 'All Levels'}
              disabled={organization === ''}
              value={orgLevel === '' ? '' : orgLevel}
              onChange={(v) => {
                setOrgLevel(v as number | '');
                setPage(1);
              }}
              options={[{ value: '', label: 'All Levels' }, ...levelOptions]}
            />
          </div>

          <div className="flex shrink-0 flex-col gap-2">
            <span className={LABEL}>Status</span>
            <div className="flex items-center gap-1 rounded-lg bg-[#f1f5f9] p-1">
              {(['active', 'inactive'] as const).map((s) => {
                const selected = status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setStatus(selected ? '' : s);
                      setPage(1);
                    }}
                    className={[
                      'rounded-md px-6 py-1.5 text-sm font-medium capitalize transition-colors',
                      selected ? 'bg-brand text-white shadow-btn' : 'text-[#475569] hover:text-brand',
                    ].join(' ')}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={clearFilters}
            className="h-[38px] shrink-0 rounded-md bg-[#e2e8f0] px-6 text-sm font-semibold text-[#334155] transition-colors hover:brightness-95"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Data table */}
      <div className="overflow-hidden rounded-xl border border-[#e2e8f0] bg-white shadow-card">
        <ConfigProvider theme={TABLE_THEME}>
          <Table<AppUser>
            className="chunaav-table"
            columns={columns}
            dataSource={items}
            rowKey="id"
            pagination={false}
            loading={isFetching}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>

        {/* Footer / pagination */}
        <Pagination
          rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
          rangeEnd={Math.min(page * pageSize, total)}
          total={total}
          noun="users"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>

      <ConfirmDeleteModal
        open={userToDelete !== null}
        title="Delete User"
        message={
          <>
            Are you sure you want to delete{' '}
            <span className="font-semibold text-ink">
              “{userToDelete ? userName(userToDelete) : ''}”
            </span>
            ? This action is permanent and cannot be undone.
          </>
        }
        loading={isDeleting}
        onClose={() => setUserToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
